import { RefreshCw, X } from 'lucide-react'
import { useAppVersion } from '@/hooks/useAppVersion'
import { Button } from '@/components/ui/button'

/**
 * Tells somebody their tab is running an old version of the app.
 *
 * A row of its own, above everything, rather than something floating over the
 * top. It was floating first, and at phone width the pill spanned 317 of 390
 * pixels — covering the survey screen's back link AND its sync indicator, the
 * one control that says whether a morning's work has left the device. An
 * update notice that hides whether the work is safe has its priorities exactly
 * backwards.
 *
 * Taking a row costs a strip of screen on every page and covers nothing. The
 * full-viewport screens shrink to fit it because they measure themselves
 * against what is left rather than against the viewport.
 *
 * Neutral surface with color only on the action, per the design system: this
 * is information, not a warning. Nothing is wrong, there is simply something
 * newer. A red or amber bar would say otherwise and earn itself a reputation
 * for crying wolf.
 */
export function UpdateBanner() {
  const { isStale, dismiss, refresh } = useAppVersion()

  if (!isStale) return null

  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-2 border-b border-default bg-subtle px-3 py-1.5"
    >
      <RefreshCw className="h-3.5 w-3.5 shrink-0 text-brand" />
      <span className="min-w-0 flex-1 truncate text-sm">
        A newer version is available
        {/* The reason, not just the fact. Somebody mid-survey deciding whether
            to interrupt themselves deserves to know it is about their work
            being saved correctly. Dropped on a narrow screen, where the
            headline has to win the space. */}
        <span className="hidden text-muted-text sm:inline">
          {' '}
          — refresh to make sure new work saves correctly
        </span>
      </span>
      <Button size="sm" className="shrink-0" onClick={refresh}>
        Refresh
      </Button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss until next time"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-text transition-colors hover:bg-subtle hover:text-secondary-text"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
